import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { namespaceToPathByLng, preloadedI18nResources } from './resources.jsx';
import { useLanguageTransitionStore } from './zustand/language-transition-store';

export const LANGUAGE_STORAGE_KEY = 'language';

const SUPPORTED_LANGUAGES = ['en', 'ar'];

const loadedNamespaces = new Set();

function normalizeLanguageCode(lng) {
  if (!lng || typeof lng !== 'string') {
    return 'en';
  }
  const code = lng.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(code) ? code : 'en';
}

/** Read persisted UI language; falls back to `en` if missing or unsupported. */
export function readSavedLanguage() {
  if (typeof window === 'undefined') {
    return 'en';
  }
  try {
    const raw = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (raw) {
      return normalizeLanguageCode(raw);
    }
  } catch {
    // ignore quota / private mode
  }
  return 'en';
}

export function applyDocumentDirection(lng) {
  if (typeof document === 'undefined') {
    return;
  }
  const code = normalizeLanguageCode(lng);
  const isRTL = code === 'ar';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = code;
}

function resolvedLng() {
  return normalizeLanguageCode(i18n.language);
}

export async function loadNamespace(namespace, lng = resolvedLng()) {
  const key = `${lng}:${namespace}`;
  if (loadedNamespaces.has(key)) {
    return;
  }
  const loaders = namespaceToPathByLng[lng] || namespaceToPathByLng.en;
  const loader = loaders[namespace];
  if (!loader) {
    return;
  }
  const mod = await loader();
  const translations = mod.default;
  i18n.addResourceBundle(lng, namespace, translations, true, true);
  loadedNamespaces.add(key);
}

export async function loadRemainingNamespaces(lng = resolvedLng()) {
  const loaders = namespaceToPathByLng[lng] || namespaceToPathByLng.en;
  await Promise.all(Object.keys(loaders).map(ns => loadNamespace(ns, lng)));
}

export async function changeAppLanguage(lng) {
  const nextLanguage = normalizeLanguageCode(lng);
  const currentLanguage = normalizeLanguageCode(i18n.resolvedLanguage || i18n.language);

  if (nextLanguage === currentLanguage) {
    return;
  }

  const { setIsChangingLanguage } = useLanguageTransitionStore.getState();
  setIsChangingLanguage(true);

  try {
    // Load the target language's bundles *before* switching - otherwise the
    // re-render triggered by changeLanguage runs against empty namespaces and
    // returnObjects lookups come back as the key string (e.g. `.map` crashes).
    await loadRemainingNamespaces(nextLanguage);
    await i18n.changeLanguage(nextLanguage);
    if (typeof window !== 'undefined') {
      await new Promise(resolve => requestAnimationFrame(() => resolve()));
    }
  } finally {
    setIsChangingLanguage(false);
  }
}

const savedLanguage = readSavedLanguage();

applyDocumentDirection(savedLanguage);

i18n.use(initReactI18next).init({
  resources: preloadedI18nResources,
  lng: savedLanguage,
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: [
    'common',
    'layout',
    'physiotherapists',
    'clinicsAdmins',
    'patients',
    'settings',
    'staff',
    'exercise',
    'auth',
    'help',
    'home',
    'appointments',
    'terms',
  ],
  defaultNS: 'common',
  interpolation: {
    escapeValue: false,
  },
});

i18n.on('languageChanged', lng => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    }
  } catch {
    // ignore
  }
  applyDocumentDirection(lng);
  void loadRemainingNamespaces();
});

export default i18n;
